import type { RootStateOrAny } from 'react-redux'

import { isServer } from '@library/environment'
import { BROWSER_ERROR_KEYS, instrumentBrowserError } from '@library/observability/browser'

import { actionTypes } from 'actions/actionTypes'
import { basketPreviewOrderChange } from 'actions/basket'
import statusActions from 'actions/status'
import { createPreviewOrder } from 'apis/orders'
import basketConfig from 'config/basket'
import type { CheckoutActionCreator } from 'routes/Checkout/models/ReduxTypes'
import {
  getCouldBasketBeExpired,
  getOrderDetails,
  getSlotForBoxSummaryDeliveryDays,
} from 'routes/Menu/selectors/order'
import { getAuthUserId } from 'selectors/auth'
import logger from 'utils/logger'

const basketExpiredError = {
  message: 'Missing data, persistent basket might be expired',
  code: 'basket-expired',
}

export const checkoutCreatePreviewOrder: CheckoutActionCreator =
  () => async (dispatch, getState) => {
    const userId = getAuthUserId(getState())
    const [slot, slotId] = getSlotForBoxSummaryDeliveryDays(getState())

    dispatch(statusActions.pending(actionTypes.BASKET_PREVIEW_ORDER_CHANGE, true))
    dispatch(statusActions.error(actionTypes.BASKET_PREVIEW_ORDER_CHANGE, null))

    const state = getState()

    if (!slot) {
      logger.error({ message: `Can't find any slot with id: ${slotId}`, actor: userId })

      dispatch(statusActions.pending(actionTypes.BASKET_PREVIEW_ORDER_CHANGE, false))
      dispatch(
        statusActions.error(
          actionTypes.BASKET_PREVIEW_ORDER_CHANGE,
          `Can't find any slot with id: ${slotId}`,
        ),
      )

      return
    }

    const orderDetails = getOrderDetails(state as RootStateOrAny)
    const recipeChoices = orderDetails.recipe_choices ?? []
    const hasMinimumRecipes = recipeChoices.length >= basketConfig.minRecipesNum
    const { routing: { locationBeforeTransitions: { pathname: path } = {} } = {} } =
      state as RootStateOrAny
    const isAuthenticated = Boolean(userId)
    const isBasketExpired = getCouldBasketBeExpired(state as RootStateOrAny)
    const couldBasketBeExpired = isBasketExpired || !hasMinimumRecipes

    if (couldBasketBeExpired) {
      const reason = isBasketExpired ? 'basket-expired' : 'insufficient-recipes'

      logger.warning({
        message: basketExpiredError.message,
        actor: userId,
        extra: {
          deliveryDayId: orderDetails.delivery_day_id,
          deliverySlotId: orderDetails.delivery_slot_id,
          recipeChoices,
          path,
          serverSide: isServer(),
        },
      })
      instrumentBrowserError(BROWSER_ERROR_KEYS.CHECKOUT_PREVIEW_BASKET_ERROR, {
        isAuthenticated,
        path,
        deliveryDayId: orderDetails.delivery_day_id,
        deliverySlotId: orderDetails.delivery_slot_id,
        recipeChoiceCount: recipeChoices.length,
        reason,
      })

      dispatch(statusActions.error(actionTypes.BASKET_PREVIEW_ORDER_CHANGE, basketExpiredError))
      dispatch(statusActions.pending(actionTypes.BASKET_PREVIEW_ORDER_CHANGE, false))

      return
    }

    try {
      const { data: previewOrder } = await createPreviewOrder(orderDetails)

      const { order } = previewOrder

      dispatch(basketPreviewOrderChange(String(order.id), order.boxId))

      dispatch(statusActions.error(actionTypes.BASKET_PREVIEW_ORDER_CHANGE, null))
    } catch (e: any) {
      const { message, code } = e
      logger.warning(message)

      logger.error({
        message: 'createPreviewOrder failed, logging error below...',
        actor: userId,
        extra: {
          orderDetails,
          path,
          serverSide: isServer(),
        },
      })
      logger.error(e)
      instrumentBrowserError(BROWSER_ERROR_KEYS.CHECKOUT_PREVIEW_REQUEST_ERROR, {
        isAuthenticated,
        path,
        deliveryDayId: orderDetails.delivery_day_id,
        deliverySlotId: orderDetails.delivery_slot_id,
        recipeChoiceCount: recipeChoices.length,
        code,
        message,
      })

      dispatch(statusActions.error(actionTypes.BASKET_PREVIEW_ORDER_CHANGE, { message, code }))
    }

    dispatch(statusActions.pending(actionTypes.BASKET_PREVIEW_ORDER_CHANGE, false))
  }
